
import { createClient } from '@supabase/supabase-js'
import type { Database } from './types'
import { logger } from '@/utils/logger'

// Production credentials - ONLY PRODUCTION NOW
const PRODUCTION_SUPABASE_URL = "https://vumkvbibhnptdiyvqvap.supabase.co"
const PRODUCTION_SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZ1bWt2YmliaG5wdGRpeXZxdmFwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzQ4ODUwNjEsImV4cCI6MjA1MDQ2MTA2MX0.aFqy9MzoLW2zxEFPA9SEqxV0QFEKpyHpmI_t_IzqSUs"

// Simple production-only client
export const supabase = createClient<Database>(PRODUCTION_SUPABASE_URL, PRODUCTION_SUPABASE_ANON_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage,
    flowType: 'pkce'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

logger.info('🚀 Supabase client initialized');

// Session management utilities
export async function ensureUserSession(): Promise<boolean> {
  try {
    let retries = 0;
    const maxRetries = 3;
    
    while (retries < maxRetries) {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        logger.error("Error getting session:", error);
        return false;
      }
      
      if (!session?.user) {
        logger.debug(`No user session found (attempt ${retries + 1}/${maxRetries})`);
        
        if (retries < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
          continue;
        }
        return false;
      }
      
      try {
        await supabase.rpc('set_current_user_id');
        logger.debug("✅ User session confirmed and set");
        return true;
      } catch (rpcError) {
        logger.warn("Warning: Could not set session user ID via RPC:", rpcError);
        return true;
      }
    }
    
    return false;
  } catch (error) {
    logger.error("Error ensuring user session:", error);
    return false;
  }
}

// Session setup wrapper for database operations
export async function withSessionSetup<T>(operation: () => Promise<T>): Promise<T | null> {
  try {
    const sessionReady = await ensureUserSession();
    if (!sessionReady) {
      logger.error("❌ CRITICAL: Session setup failed - operation aborted");
      return null;
    }
    
    logger.debug("✅ Session confirmed - proceeding with operation");
    return await operation();
  } catch (error) {
    logger.error("❌ Error in withSessionSetup:", error);
    return null;
  }
}

// Get current client user ID
export async function getCurrentClientUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    logger.error("Error getting current user ID:", error);
    return null;
  }
}
